// Bridge between the Discord Social SDK (native Capacitor plugin) and the app.
// No-ops in a regular browser PWA, exactly like HealthConnectService.
//
// Rich Presence can only be published from a native build: the Social SDK is a
// native binary and there is no REST endpoint to set a user's presence. The
// plugin owns the Discord client lifecycle; this module decides WHAT to publish
// and when, and brokers tokens with the backend.
//
// The app is a confidential OAuth client, so the device never holds a Discord
// credential: it obtains an authorization code, the backend exchanges it using
// the client secret, and only a short-lived access token comes back. The
// refresh token stays server-side, which also means a link survives reinstalls
// and follows the user across devices.
//
// Discord renders the elapsed timer itself from `startTimestamp`, so a running
// workout needs no periodic updates — we only push on meaningful changes.

const PLUGIN_NAME = 'DiscordPresence';

// Presence updates are rate limited by Discord. Coalesce bursts (a user tapping
// through several sets quickly) and always flush the final state.
const MIN_UPDATE_INTERVAL_MS = 15000;

let lastSentAt = 0;
let pendingActivity = null;
let flushTimer = null;
// Kept so the SDK's expiry warning can be answered without the caller having to
// stay mounted to hand us a backend client again.
let tokenBroker = null;
let expiryListenerBound = false;

function isNative() {
  return (
    typeof window !== 'undefined' &&
    !!window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === 'function' &&
    window.Capacitor.isNativePlatform()
  );
}

function getPlugin() {
  if (!isNative()) return null;
  return window.Capacitor?.Plugins?.[PLUGIN_NAME] || null;
}

/** True when the native plugin is present in this build. */
function isSupported() {
  return !!getPlugin();
}

/**
 * Whether this device can publish presence at all.
 * @returns {Promise<{available: boolean, reason?: string}>}
 */
async function isAvailable() {
  const plugin = getPlugin();
  if (!plugin) return { available: false, reason: isNative() ? 'plugin-missing' : 'not-native' };
  try {
    const res = await plugin.isAvailable();
    return { available: res?.available === true, reason: res?.reason };
  } catch (err) {
    console.warn('[DiscordPresence] isAvailable failed:', err?.message || err);
    return { available: false, reason: err?.message || 'error' };
  }
}

/**
 * Current link state.
 * @returns {Promise<{connected: boolean, username?: string}>}
 */
async function getStatus() {
  const plugin = getPlugin();
  if (!plugin) return { connected: false };
  try {
    const res = await plugin.getStatus();
    return { connected: res?.connected === true, username: res?.username || null };
  } catch (err) {
    console.warn('[DiscordPresence] getStatus failed:', err?.message || err);
    return { connected: false };
  }
}

/**
 * Subscribe to link-state changes. Connecting is asynchronous and can complete
 * well after the first getStatus().
 * @returns {() => void} unsubscribe
 */
function addStatusListener(handler) {
  const plugin = getPlugin();
  if (!plugin) return () => {};
  let handle;
  try {
    handle = plugin.addListener('statusChanged', event => {
      handler({ connected: event?.connected === true, username: event?.username || null });
    });
  } catch (err) {
    console.warn('[DiscordPresence] addStatusListener failed:', err?.message || err);
    return () => {};
  }
  // Runs from an effect cleanup, so a throw here would tear down the React root.
  // addListener hands back a promise on web but the handle itself on native.
  return () => {
    try {
      if (typeof handle?.then === 'function') handle.then(sub => sub?.remove()).catch(() => {});
      else handle?.remove?.();
    } catch (err) {
      console.warn('[DiscordPresence] removeStatusListener failed:', err?.message || err);
    }
  };
}

// Discord warns once before an access token expires. Since the backend mints
// them, renewal is just "ask for another one".
//
// Never allowed to throw: this is a nice-to-have, and letting it fail would
// otherwise take down whichever call was about to connect the client.
function bindExpiryListener(backendService) {
  tokenBroker = backendService;
  const plugin = getPlugin();
  if (!plugin || expiryListenerBound) return;
  expiryListenerBound = true;
  try {
    const handle = plugin.addListener('tokenExpiring', async () => {
      if (!tokenBroker) return;
      try {
        const token = await tokenBroker.getDiscordAccessToken();
        if (token?.access_token) await plugin.applyToken({ accessToken: token.access_token });
      } catch (err) {
        console.warn('[DiscordPresence] token renewal failed:', err?.message || err);
      }
    });
    if (handle && typeof handle.catch === 'function') handle.catch(() => {});
  } catch (err) {
    expiryListenerBound = false;
    console.warn('[DiscordPresence] could not bind tokenExpiring:', err?.message || err);
  }
}

/**
 * Link a Discord account: run the OAuth flow on the device, have the backend
 * exchange the resulting code, then bring the client up with the access token.
 * Deep-links into the Discord app when installed, otherwise opens a browser.
 *
 * @param {Object} backendService - an authenticated BackendService instance.
 */
async function link(backendService) {
  const plugin = getPlugin();
  if (!plugin) return { ok: false, reason: 'unsupported' };
  try {
    const grant = await plugin.authorize();
    const token = await backendService.linkDiscordAccount({
      code: grant?.code,
      code_verifier: grant?.codeVerifier,
      redirect_uri: grant?.redirectUri
    });
    if (!token?.access_token) return { ok: false, reason: 'no access token' };
    const res = await plugin.applyToken({ accessToken: token.access_token });
    bindExpiryListener(backendService);
    return { ok: res?.connected === true, username: res?.username || null };
  } catch (err) {
    console.error('[DiscordPresence] link failed:', err);
    return { ok: false, reason: err?.message || 'error' };
  }
}

/**
 * Bring the client up for an already-linked user, without any interaction.
 * Cheap to call repeatedly: no-ops once connected.
 *
 * @param {Object} backendService - an authenticated BackendService instance.
 */
async function ensureConnected(backendService) {
  const plugin = getPlugin();
  if (!plugin) return { ok: false, reason: 'unsupported' };
  const current = await getStatus();
  if (current.connected) return { ok: true, username: current.username };
  try {
    const token = await backendService.getDiscordAccessToken();
    if (!token?.access_token) {
      console.warn('[DiscordPresence] backend returned no access token:',
        token ? Object.keys(token).join(',') : 'null');
      return { ok: false, reason: 'not-linked' };
    }
    const res = await plugin.applyToken({ accessToken: token.access_token });
    bindExpiryListener(backendService);
    return { ok: res?.connected === true, username: res?.username || null };
  } catch (err) {
    console.warn('[DiscordPresence] ensureConnected failed:', err?.message || err);
    return { ok: false, reason: err?.message || 'error' };
  }
}

/**
 * Unlink the account. Clears presence and drops the server-side tokens, so the
 * link does not come back on the next launch or on another device.
 */
async function unlink(backendService) {
  const plugin = getPlugin();
  cancelPending();
  tokenBroker = null;
  try {
    if (plugin) await plugin.disconnect();
    await backendService.unlinkDiscordAccount();
    return { ok: true };
  } catch (err) {
    console.error('[DiscordPresence] unlink failed:', err);
    return { ok: false, reason: err?.message || 'error' };
  }
}

function cancelPending() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  pendingActivity = null;
}

async function sendNow(activity) {
  const plugin = getPlugin();
  if (!plugin) return { ok: false, reason: 'unsupported' };
  lastSentAt = Date.now();
  try {
    await plugin.setActivity(activity);
    return { ok: true };
  } catch (err) {
    console.warn('[DiscordPresence] setActivity failed:', err?.message || err);
    return { ok: false, reason: err?.message || 'error' };
  }
}

/**
 * Publish an activity, throttled. The most recent payload always wins and is
 * guaranteed to be sent once the interval elapses.
 */
async function setActivity(activity) {
  if (!getPlugin() || !activity) return { ok: false, reason: 'unsupported' };

  const elapsed = Date.now() - lastSentAt;
  if (elapsed >= MIN_UPDATE_INTERVAL_MS) {
    cancelPending();
    return sendNow(activity);
  }

  pendingActivity = activity;
  if (!flushTimer) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      const next = pendingActivity;
      pendingActivity = null;
      if (next) sendNow(next);
    }, MIN_UPDATE_INTERVAL_MS - elapsed);
  }
  return { ok: true, queued: true };
}

/** Clear presence immediately, bypassing the throttle. */
async function clearActivity() {
  const plugin = getPlugin();
  cancelPending();
  if (!plugin) return { ok: false, reason: 'unsupported' };
  try {
    await plugin.clearActivity();
    return { ok: true };
  } catch (err) {
    console.warn('[DiscordPresence] clearActivity failed:', err?.message || err);
    return { ok: false, reason: err?.message || 'error' };
  }
}

export const DEFAULT_PRESENCE_SETTINGS = {
  enabled: false,
  show_workout_name: true,
  show_exercise: true,
  show_sets: true
};

/**
 * Turn live workout state into a Discord activity payload.
 *
 * Pure and side-effect free so it can be unit tested. Returns null when the
 * user hasn't opted in, so callers can treat null as "publish nothing".
 *
 * @param {Object} workout - { name, started_at }
 * @param {Object} live - { exerciseName, completedSets, totalSets }
 * @param {Object} settings - Discord presence settings for the user.
 */
function buildWorkoutActivity(workout, live = {}, settings = DEFAULT_PRESENCE_SETTINGS) {
  if (!settings?.enabled || !workout) return null;

  const { exerciseName, completedSets, totalSets } = live;

  const detailParts = [];
  if (settings.show_exercise && exerciseName) {
    detailParts.push(exerciseName);
  }
  if (settings.show_sets && Number.isFinite(completedSets) && Number.isFinite(totalSets) && totalSets > 0) {
    detailParts.push(`Set ${Math.min(completedSets + 1, totalSets)} of ${totalSets}`);
  }

  const stateParts = [];
  if (settings.show_workout_name && workout.name) {
    stateParts.push(workout.name);
  }

  // Discord hides empty lines, but a presence with no detail at all reads as a
  // bare "Playing Kata.fit" — give it something meaningful.
  const details = detailParts.join(' · ') || (stateParts.length ? 'Working out' : 'In a workout');
  const state = stateParts.join(' · ') || undefined;

  const startMs = workout.started_at ? new Date(workout.started_at).getTime() : NaN;

  return {
    details,
    state,
    // Unix seconds. Discord renders the count-up timer client side.
    startTimestamp: Number.isFinite(startMs) ? Math.floor(startMs / 1000) : undefined,
    largeImage: 'kata-logo',
    largeText: 'Kata.fit'
  };
}

const DiscordPresenceService = {
  isNative,
  isSupported,
  isAvailable,
  getStatus,
  addStatusListener,
  link,
  unlink,
  ensureConnected,
  setActivity,
  clearActivity,
  buildWorkoutActivity,
  DEFAULT_PRESENCE_SETTINGS,
  MIN_UPDATE_INTERVAL_MS
};

export default DiscordPresenceService;
